import { alpha, type SxProps, Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';

/** Warm stripe / header wash derived from brand primary (#9C5A37). */
const STRIPE_ALPHA = 0.1;
const HEADER_ALPHA = 0.14;

const TEXT_ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify', 'start', 'end']);
const VERTICAL_ALIGN_VALUES = new Set(['top', 'middle', 'bottom', 'baseline']);

const INLINE_STYLE_KEYS: Record<string, keyof CSSProperties> = {
  'text-align': 'textAlign',
  'vertical-align': 'verticalAlign',
  'background-color': 'backgroundColor',
  'border-color': 'borderColor',
  'border-style': 'borderStyle',
  'border-width': 'borderWidth',
  'border-collapse': 'borderCollapse',
  'border-spacing': 'borderSpacing',
  'border-top': 'borderTop',
  'border-right': 'borderRight',
  'border-bottom': 'borderBottom',
  'border-left': 'borderLeft',
  width: 'width',
  height: 'height',
  padding: 'padding',
  margin: 'margin',
  border: 'border',
  color: 'color',
};

const CELL_STYLE_KEYS: (keyof CSSProperties)[] = [
  'textAlign',
  'verticalAlign',
  'backgroundColor',
  'border',
  'borderColor',
  'borderStyle',
  'borderWidth',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'padding',
  'width',
  'height',
  'color',
];

export function parseTinyMceInlineStyle(style?: string): CSSProperties {
  if (!style) return {};
  const result: CSSProperties = {};
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon === -1) continue;
    const rawProp = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (!value) continue;
    const mapped = INLINE_STYLE_KEYS[rawProp];
    if (mapped) {
      (result as Record<string, string>)[mapped] = value;
    }
  }
  return result;
}

function normalizeCssMeasurement(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
}

function alignmentFromAttribs(attribs: Record<string, string>): Pick<CSSProperties, 'textAlign'> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  if (inline.textAlign) return { textAlign: inline.textAlign };

  const align = attribs.align?.toLowerCase();
  if (align && TEXT_ALIGN_VALUES.has(align)) {
    return { textAlign: align as CSSProperties['textAlign'] };
  }
  return {};
}

function verticalAlignFromAttribs(attribs: Record<string, string>): Pick<CSSProperties, 'verticalAlign'> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  if (inline.verticalAlign) return { verticalAlign: inline.verticalAlign };

  const valign = attribs.valign?.toLowerCase();
  if (valign && VERTICAL_ALIGN_VALUES.has(valign)) {
    return { verticalAlign: valign as CSSProperties['verticalAlign'] };
  }
  return {};
}

function pickCellInlineStyles(inline: CSSProperties): CSSProperties {
  const picked: CSSProperties = {};
  for (const key of CELL_STYLE_KEYS) {
    if (inline[key] != null) {
      (picked as Record<string, unknown>)[key] = inline[key];
    }
  }
  return picked;
}

function legacyBackgroundColor(attribs: Record<string, string>): string | undefined {
  return attribs.bgcolor ?? attribs.background;
}

/** Outer wrapper spacing for rich-text tables. */
export const richTextTableOuterSx: SxProps<Theme> = {
  mb: 2,
  maxWidth: '100%',
  '&:last-child': { mb: 0 },
};

/** Corner radius for rich-text table surfaces (px string — MUI scales bare numbers). */
const RICH_TEXT_TABLE_BORDER_RADIUS = '4px';

/** Elevated, rounded surface for parsed TinyMCE tables. */
export const richTextTablePaperSx: SxProps<Theme> = {
  borderRadius: RICH_TEXT_TABLE_BORDER_RADIUS,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  maxWidth: '100%',
};

/** Elevated callout surface for parsed TinyMCE blockquotes (notes / alerts). */
export const richTextCalloutPaperSx: SxProps<Theme> = {
  borderRadius: RICH_TEXT_TABLE_BORDER_RADIUS,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  maxWidth: '100%',
  p: 2,
  mb: 2,
  borderLeft: 4,
  borderColor: 'primary.main',
  '&:last-child': { mb: 0 },
  '& > :last-child': { mb: 0 },
};

/** Cell borders between rows/columns only — no outer frame (Paper elevation handles that). */
function richTextTableInnerCellBorderSx(
  borderColor: string = 'divider',
): Record<string, unknown> {
  return {
    border: 0,
    borderRight: 1,
    borderBottom: 1,
    borderColor,
    borderStyle: 'solid',
  };
}

const richTextTableInnerCellBorderTrimSx: SxProps<Theme> = {
  '& .MuiTableCell-root:last-of-type': { borderRight: 0 },
  '& tbody tr:last-of-type .MuiTableCell-root': { borderBottom: 0 },
};

export const richTextTableInnerContainerSx: SxProps<Theme> = {
  maxWidth: '100%',
};

/** Earthy zebra rows and grid lines using theme palette (not default MUI blue action tones). */
export function richTextTableStripedSx(): SxProps<Theme> {
  return (theme => ({
    '& .MuiTableCell-root': richTextTableInnerCellBorderSx(),
    ...(richTextTableInnerCellBorderTrimSx as object),
    '& thead .MuiTableCell-root': {
      backgroundColor: alpha(theme.palette.primary.main, HEADER_ALPHA),
      color: 'text.primary',
      fontWeight: 600,
    },
    '& tbody tr:nth-of-type(odd) .MuiTableCell-root': {
      backgroundColor: alpha(theme.palette.primary.main, STRIPE_ALPHA),
    },
    '& tbody tr:nth-of-type(even) .MuiTableCell-root': {
      backgroundColor: theme.palette.background.paper,
    },
  })) as SxProps<Theme>;
}

export function tableContainerSxFromAttribs(attribs: Record<string, string>): SxProps<Theme> {
  const align = attribs.align?.toLowerCase();
  if (align === 'center') {
    return { display: 'flex', justifyContent: 'center' };
  }
  if (align === 'right') {
    return { display: 'flex', justifyContent: 'flex-end' };
  }
  return {};
}

export function tableSxFromAttribs(attribs: Record<string, string>): SxProps<Theme> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  const legacyBg = legacyBackgroundColor(attribs);

  const sx: Record<string, unknown> = {
    maxWidth: '100%',
    tableLayout: 'fixed',
    borderCollapse: inline.borderCollapse ?? 'collapse',
    width: inline.width ?? (attribs.width ? normalizeCssMeasurement(attribs.width) : '100%'),
  };

  if (inline.height || attribs.height) {
    sx.height = inline.height ?? normalizeCssMeasurement(attribs.height ?? '');
  }

  if (inline.borderSpacing || attribs.cellspacing) {
    sx.borderSpacing =
      inline.borderSpacing ?? normalizeCssMeasurement(attribs.cellspacing ?? '');
  }

  if (inline.backgroundColor || legacyBg) {
    sx.backgroundColor = inline.backgroundColor ?? legacyBg;
  }

  const cellRoot: Record<string, unknown> = {};
  const cellPadding = attribs.cellpadding
    ? normalizeCssMeasurement(attribs.cellpadding)
    : undefined;
  if (cellPadding) {
    cellRoot.padding = cellPadding;
  }

  const legacyBorder = attribs.border;
  const hasCellBorder = Boolean(
    legacyBorder || inline.border || inline.borderWidth || inline.borderStyle,
  );
  if (hasCellBorder) {
    const borderColor =
      inline.borderColor && inline.borderColor.startsWith('#')
        ? inline.borderColor
        : 'divider';
    Object.assign(cellRoot, richTextTableInnerCellBorderSx(borderColor));
  }

  if (Object.keys(cellRoot).length > 0) {
    sx['& .MuiTableCell-root'] = cellRoot;
  }

  return sx as SxProps<Theme>;
}

export function tableRowSxFromAttribs(attribs: Record<string, string>): SxProps<Theme> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  const legacyBg = legacyBackgroundColor(attribs);
  if (!inline.backgroundColor && !legacyBg) return {};
  return { backgroundColor: inline.backgroundColor ?? legacyBg };
}

export function tableCellSxFromAttribs(
  attribs: Record<string, string>,
  head = false,
): SxProps<Theme> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  const legacyBg = legacyBackgroundColor(attribs);

  return {
    ...pickCellInlineStyles(inline),
    ...alignmentFromAttribs(attribs),
    ...verticalAlignFromAttribs(attribs),
    ...(legacyBg || inline.backgroundColor
      ? { backgroundColor: inline.backgroundColor ?? legacyBg }
      : {}),
    ...(head ? { fontWeight: 600 } : {}),
  };
}

/** Styles TinyMCE is allowed to persist on table markup. */
export const TINYMCE_TABLE_VALID_STYLES =
  'border,border-width,border-color,border-style,border-collapse,border-spacing,width,height,padding,margin,background-color,vertical-align,text-align';
